// src/prf/prfActuarial.js
// Resolve the county base value RMA actually used for a block of PRF acres.
//
// ⚠ THE PREVIOUS RULE WAS `type_code=030` AND `max()` AND IT WAS WRONG TWICE.
// Measured over the live book on 2026-08-24, 22 of 88 priceable blocks missed
// the AIP's stored liability, and the misses landed on REPEATED ratios rather
// than scattering — x6.642 on three separate policies, x1.189 on three,
// x1.199-1.202 on three. Both causes were ours:
//   · `prf_county_base_values` carries TWO INTENDED USES, type 007 GRAZING and
//     type 030 HAYING, and grazing runs about a fifth of haying (grid 27210:
//     $57.50-67.30 against $282-333). Hardcoding haying priced grazing acres
//     six to eleven times too high.
//   · a PRF grid spans several COUNTIES with different values, and `max()` took
//     the highest rather than the policy's own. 333/282 = 1.18.
//
// THE ACTUARIAL IDENTITY IS THE WHOLE KEY AND NOTHING MAY BE ASSUMED FROM IT:
//
//     crop year x state x county x grid x intended use (type) x practice
//
// Measured, not assumed: the CBV does NOT vary by interval (one value across
// all eleven for a given cell) and it DOES vary by practice in 4,876 of 14,628
// county/type cells — 33% — so practice cannot be waved away either.
//
// ⚠ EVERY ANSWER CARRIES ITS PROVENANCE, and an answer that cannot be reached
// from authoritative data is a REFUSAL, never a favourable pick. The three
// refusals are distinct because they need different fixes:
//     PRF_COUNTY_UNRESOLVED     the insured county is not established
//     PRF_USE_UNRESOLVED        haying vs grazing is not established
//     PRF_PRACTICE_UNRESOLVED   several practices, and they disagree on value
//
// ⚠ AND A POLICY MAY CARRY BOTH USES. PRF is written per (grid, intended use,
// practice), so grazing and haying acres can sit on one policy — six do in this
// book. Resolution is therefore per BLOCK, never per policy.

const LAB = ["JANFEB", "FEBMAR", "MARAPR", "APRMAY", "MAYJUN", "JUNJUL", "JULAUG", "AUGSEP", "SEPOCT", "OCTNOV", "NOVDEC"];

// RMA's intended-use codes for plan 13, read off the actuarial table itself.
const INTENDED_USE = { "007": "GRAZING", "030": "HAYING" };
const USE_TO_TYPE = { GRAZING: "007", HAYING: "030" };

const SOURCE = {
  // ⚠ A LAST RESORT, NOT A PREFERENCE. M13's protection-per-acre is rounded
  // to the dollar, so a price built on it runs a few cents per acre high.
  M13_PROTECTION: "M13_PROTECTION_PER_ACRE_DOLLAR_ROUNDED",
  M13_IDENTITY: "M13_IDENTITY",                 // county + use from M13, value looked up
  DERIVED_IDENTITY: "DERIVED_IDENTITY",         // use solved from the stored liability
  // The solve, used BECAUSE M13 contradicted the AIP's own liability. Same
  // arithmetic as DERIVED_IDENTITY; a different thing to report.
  DERIVED_OVER_M13: "DERIVED_OVER_INCONSISTENT_M13",
  // The published value, with the block's SHARE or PRODUCTIVITY FACTOR read off
  // the AIP's own line liabilities because the stored figure could not
  // reproduce them. See reconcileLineFactors.
  M13_IDENTITY_LINE_FACTOR: "M13_IDENTITY_LINE_FACTOR",
};

const round2 = (x) => Math.round(x * 100) / 100;

// ⚠ TWO DIFFERENT STATE ENCODINGS MEET HERE AND NEITHER SIDE IS WRONG.
// `policies.state` is POSTAL ("NY"), `prf_county_base_values.state_code` is
// FIPS ("36"), and `field_prf_intervals.county_fips` is the 3-digit COUNTY
// code alone. Keying one against the other matched nothing and every block
// silently fell back to M13's dollar-rounded per-acre figure — which still
// produced a plausible number, which is why it read as "rounding" rather than
// as a failed lookup. Only the states this book writes PRF in are listed;
// anything else returns null and the block refuses rather than guessing.
const STATE_FIPS = { NY: "36", PA: "42", OH: "39", MI: "26" };
const stateFips = (v) => {
  if (v == null) return null;
  const t = String(v).trim().toUpperCase();
  // Already a two-digit FIPS. ⚠ The backslash here is load-bearing and was
  // missing until 2026-09-09 — /^d{2}$/ is a literal "d", so "36" fell through to
  // the postal lookup, missed, and returned null. LATENT rather than live:
  // measured that day, every caller reaching this supplies `policies.state`
  // (postal), so the branch never decided anything. It matters the moment one
  // does — `prfQuoteDraft.newDraft` already defaults `stateCode` to "36" — and a
  // null state makes cbvFromTable miss and the block fall through to the solve,
  // which is a quieter wrong answer than a throw.
  if (/^\d{2}$/.test(t)) return t;
  return STATE_FIPS[t] || null;
};

// ── what the AIP itself said, per (policy, crop year, grid, interval) ──
// `field_prf_intervals` is the M13 per-CLU PRF record. It carries the county
// FIPS, the intended use and — better than a CBV — the PROTECTION PER ACRE RMA
// used, which is exactly the figure the quote engine needs. Where it speaks, it
// is the answer; nothing is derived on top of it.
function indexFieldPrf(rows) {
  const byBlock = {};
  for (const r of rows || []) {
    const k = `${r.policy_number}|${r.crop_year}|${Number(r.grid_id)}`;
    (byBlock[k] ||= []).push(r);
  }
  return byBlock;
}

// One block's M13 answer, or null. A block is only answered when the M13 rows
// for it AGREE — a split use or a split protection inside one block means the
// block boundary is wrong, and that must surface rather than be averaged.
function m13AnswerFor(policyNumber, cropYear, grid, fpiIndex, narrow = null) {
  let rows = fpiIndex?.[`${policyNumber}|${cropYear}|${Number(grid)}`];
  if (!rows || !rows.length) return null;
  // Narrow to THIS block — its intended use and the intervals it elected.
  if (narrow?.use) rows = rows.filter((r) => r.intended_use === narrow.use);
  if (narrow?.intervals?.length) {
    const want = new Set(narrow.intervals);
    const hit = rows.filter((r) => want.has(r.interval_code));
    if (hit.length) rows = hit;
  }
  if (!rows.length) return null;
  const uses = [...new Set(rows.map((r) => r.intended_use).filter(Boolean))];
  const types = [...new Set(rows.map((r) => r.type_code).filter(Boolean))];
  const fips = [...new Set(rows.map((r) => r.county_fips).filter(Boolean))];
  const ppa = [...new Set(rows.map((r) => Number(r.protection_per_acre)).filter((v) => Number.isFinite(v) && v > 0))];
  const covs = [...new Set(rows.map((r) => Number(r.coverage_level)).filter(Number.isFinite))];
  const pfs = [...new Set(rows.map((r) => Number(r.productivity_factor)).filter(Number.isFinite))];
  return {
    rows: rows.length,
    use: uses.length === 1 ? uses[0] : null, uses,
    type_code: types.length === 1 ? types[0] : null, types,
    county_fips: fips.length === 1 ? fips[0] : null, county_fips_all: fips,
    protection_per_acre: ppa.length === 1 ? ppa[0] : null, protection_per_acre_all: ppa,
    coverage_level: covs.length === 1 ? covs[0] : null,
    productivity_factor: pfs.length === 1 ? pfs[0] : null,
  };
}

// ── the published table, indexed on its real key ──────────────────
function indexCbv(rows) {
  const idx = {};
  for (const c of rows || []) {
    const k = `${c.crop_year}|${c.state_code}|${c.county_code}|${Number(c.grid_id)}|${c.type_code}`;
    (idx[k] ||= new Map()).set(String(c.practice_code), Number(c.county_base_value));
  }
  return idx;
}

// Look the value up on the FULL identity. Practice is the one part a PRF policy
// row does not carry, so it is resolved by AGREEMENT: if every practice
// published for this cell states the same value the answer is unambiguous;
// if they disagree the block is refused rather than assigned the convenient one.
function cbvFromTable(cbvIndex, { cropYear, state, county, grid, typeCode }) {
  const m = cbvIndex?.[`${cropYear}|${state}|${county}|${Number(grid)}|${typeCode}`];
  if (!m || !m.size) return { ok: false, status: "PRF_CBV_NOT_PUBLISHED" };
  const vals = [...new Set(m.values())];
  if (vals.length > 1) {
    return { ok: false, status: "PRF_PRACTICE_UNRESOLVED",
             detail: `${m.size} practices publish ${vals.length} different values (${vals.map((v) => "$" + v).join(", ")})` };
  }
  return { ok: true, cbv: vals[0], practices: m.size };
}

// ── solving the use from what RMA already computed ────────────────
// RMA's own identity: liability = round(CBV x coverage x productivity, 2)
//                                 x acres x share x percent-of-value.
// The CBV therefore falls out of stored values, and is then MATCHED against the
// published table. This is a solve against RMA's own arithmetic, not a guess
// from acreage, crop name, interval structure or a neighbouring record — all of
// which are excluded on purpose.
function recoverCbv({ liability, acres, percentValue, coverageLevel, productivityFactor, share = 1 }) {
  const sh = share == null ? 1 : Number(share);
  if (!(liability > 0 && acres > 0 && percentValue > 0 && coverageLevel > 0 && productivityFactor > 0 && sh > 0)) return null;
  return liability / (acres * percentValue * sh) / (coverageLevel * productivityFactor);
}

// ── A LINE'S OWN SHARE AND PRODUCTIVITY FACTOR, FROM ITS OWN LIABILITY ────
//
// RMA's line liability is
//     round( round2(CBV x coverage x productivity) x acres x share x percent, 0 )
// (RI Basic Provisions 21-RI §1 "insurance value"; CP 26-RI-PRF §2). Given the
// published CBV, the stored liability on every line of a block pins
// productivity x share. Silas stores the productivity factor per line, but the
// loader never stored the SHARE (M13 P11 f[42]) — so a block written at a 60%
// share priced at 100% and read 67% high. Measured 2026-11-03 on MP-0890232,
// grid 27510 grazing: the AIP's P14 states PF 150%, its P11 states share 0.6,
// and 67.30 x 0.9 x 1.5 x 44.03 x 0.6 x 0.5 = $1,200, the billed liability.
// (PF 90% at 100% share also lands on $1,200 — the dollars cannot tell them
// apart, the carrier's record can, which is why share is tried first.)
//
// Returns the reading and NEVER guesses:
//   AGREES          the stored factors reproduce every line — nothing changes
//   DERIVED_SHARE   the share was not stored, and exactly one share in whole
//                   percents (then thousandths) reproduces every line
//   DERIVED_PF      no share fits, and exactly one whole-percent PF in 60-150%
//                   at the stored share reproduces every line
//   CONTRADICTS     nothing, or more than one thing, fits — stored values are
//                   kept and the block is flagged
// Share is tried before PF because the loader takes PF from the line's own P14
// coverage record but dropped share; a PF that disagrees with the carrier's own
// P14 is the less likely explanation.
function lineLiability(cbv, cov, pf, acres, share, pv) {
  const perAcre = Math.round(cbv * cov * pf * 100 + 1e-9) / 100;
  return Math.round(perAcre * acres * share * pv + 1e-9);
}
function reconcileLineFactors({ lines, cbv, coverageLevel, productivityFactor, share = null }) {
  const cov = Number(coverageLevel), pf = Number(productivityFactor);
  const sharedStated = share != null && Number(share) > 0;
  const sh0 = sharedStated ? Number(share) : 1;
  const use = (lines || []).filter((l) => Number(l.liability) > 0 && Number(l.acres) > 0 && Number(l.percent_value) > 0);
  if (!(cbv > 0 && cov > 0 && pf > 0) || !use.length) return { status: "UNCHECKED", share: sh0, productivity_factor: pf };
  const fits = (p, s) => use.every((l) => lineLiability(cbv, cov, p, Number(l.acres), s, Number(l.percent_value)) === Math.round(Number(l.liability)));
  if (fits(pf, sh0)) return { status: "AGREES", share: sh0, productivity_factor: pf };
  if (!sharedStated) {
    for (const step of [100, 1000]) {
      const hit = [];
      for (let k = 1; k < step; k++) if (fits(pf, k / step)) hit.push(k / step);
      if (hit.length === 1) {
        return { status: "DERIVED_SHARE", share: hit[0], productivity_factor: pf,
                 detail: `share ${(hit[0] * 100).toFixed(step === 100 ? 0 : 1)}% reproduces the AIP's liability on all ${use.length} lines; the share is not stored` };
      }
      if (hit.length > 1) break;           // several shares fit: not established
    }
  }
  const pfHit = [];
  for (let k = 60; k <= 150; k++) if (k !== Math.round(pf * 100) && fits(k / 100, sh0)) pfHit.push(k / 100);
  if (pfHit.length === 1) {
    return { status: "DERIVED_PF", share: sh0, productivity_factor: pfHit[0],
             detail: `productivity ${Math.round(pfHit[0] * 100)}% reproduces the AIP's liability on all ${use.length} lines; ${Math.round(pf * 100)}% is stored` };
  }
  return { status: "CONTRADICTS", share: sh0, productivity_factor: pf,
           detail: `the stored productivity ${Math.round(pf * 100)}% and share ${Math.round(sh0 * 100)}% do not reproduce the AIP's line liability, and no single share or productivity factor does` };
}

// Which published cells a recovered value lands on. A tolerance wide enough to
// force a match is the failure this exists to avoid, so it is tight and
// absolute — a cent of rounding on the per-acre figure, not a percentage that
// grows with the value.
function matchRecovered(cbvIndex, { cropYear, grid, recovered }, tolerance = 0.51) {
  const hits = [];
  const gridStr = String(Number(grid));
  for (const key of Object.keys(cbvIndex)) {
    const [cy, st, co, gd, ty] = key.split("|");
    if (Number(cy) !== Number(cropYear) || gd !== gridStr) continue;
    for (const [practice, v] of cbvIndex[key]) {
      if (Math.abs(v - recovered) <= tolerance) hits.push({ state: st, county: co, type_code: ty, practice, cbv: v });
    }
  }
  return hits;
}

// ── THE RESOLVER ──────────────────────────────────────────────────
// Returns { ok, cbv, protection_per_acre, use, county, source, ... } or a
// refusal carrying a status the caller must surface. It never returns a value
// it cannot attribute.
function resolveCbv(block, refs) {
  const { cropYear, policyNumber } = refs;
  const grid = Number(block.grid);
  const cov = Number(block.coverage_level), pf = Number(block.productivity_factor);
  const sh = block.share == null ? 1 : Number(block.share);
  const out = { grid, crop_year: cropYear };

  // 1. The AIP stated the protection per acre. Nothing beats that — it IS the
  //    figure the quote is built on, so no CBV, county or practice is needed.
  const m13 = m13AnswerFor(policyNumber, cropYear, grid, refs.fpiIndex,
    { use: refs.blockUse || null, intervals: refs.blockIntervals || null });
  if (m13) {
    out.m13 = { rows: m13.rows, use: m13.use, county_fips: m13.county_fips,
                protection_per_acre_stated: m13.protection_per_acre };
    // M13 gives the IDENTITY. The published table gives the VALUE.
    if (m13.type_code && m13.county_fips) {
      const st = String(m13.county_fips).length >= 5 ? String(m13.county_fips).slice(0, 2) : stateFips(refs.stateCode);
      const co = String(m13.county_fips).length >= 5 ? String(m13.county_fips).slice(-3) : String(m13.county_fips);
      const look = cbvFromTable(refs.cbvIndex, { cropYear, state: st, county: co, grid, typeCode: m13.type_code });
      if (look.ok) {
        // Check the M13 identity against the AIP's own money before trusting it.
        const implied = recoverCbv({
          liability: block.stored_liability_for_solve ?? null, acres: block.acres,
          percentValue: block.solve_percent ?? null,
          coverageLevel: cov, productivityFactor: pf, share: sh });
        const agrees = implied == null || Math.abs(implied - look.cbv) <= Math.max(0.51, look.cbv * 0.002);
        if (agrees) {
          return { ...out, ok: true, source: SOURCE.M13_IDENTITY, cbv: look.cbv,
                   protection_per_acre: round2(look.cbv * cov * pf),
                   use: m13.use, type_code: m13.type_code, county: co, state: st, practices: look.practices };
        }
        // The published value disagrees with the stored liability at the
        // stored productivity and share. Before calling M13 wrong, ask whether
        // ONE share or productivity factor closes it on every line.
        const rec = reconcileLineFactors({ lines: block.lines, cbv: look.cbv, coverageLevel: cov,
          productivityFactor: pf, share: block.share_stated ? sh : null });
        if (rec.status === "DERIVED_SHARE" || rec.status === "DERIVED_PF") {
          return { ...out, ok: true, source: SOURCE.M13_IDENTITY_LINE_FACTOR, cbv: look.cbv,
                   protection_per_acre: round2(look.cbv * cov * rec.productivity_factor),
                   use: m13.use, type_code: m13.type_code, county: co, state: st, practices: look.practices,
                   line_factor: rec };
        }
        // The file disagrees with its own liability. Fall through to the solve,
        // and carry what disagreed so it can be surfaced rather than buried.
        out.m13_inconsistent = {
          m13_use: m13.use, m13_type_code: m13.type_code,
          m13_implies_cbv: look.cbv, liability_implies_cbv: implied,
          m13_protection_per_acre: m13.protection_per_acre ?? null,
          note: "M13's stated identity does not reproduce the AIP's own stored liability; the liability is treated as authoritative",
        };
      }
      // The published cell is unusable for this county. Do NOT settle for
      // M13's dollar-rounded per-acre figure yet — fall through to the solve
      // below, which reproduces the AIP's own liability exactly.
      out.lookup_failed = look.status;
      out.lookup_detail = look.detail || null;
    }
    if (m13.uses.length > 1) {
      return { ...out, ok: false, status: "PRF_USE_UNRESOLVED",
               detail: `M13 rows for this block state ${m13.uses.join(" and ")} — the block spans two intended uses` };
    }
    // M13 knew the use but not the county — the solve below settles the value.
  }

  // 3. No M13 record. Solve the CBV from the AIP's own liability and match it
  //    against the published table. A unique landing resolves the use; anything
  //    else refuses.
  const recovered = recoverCbv({
    liability: block.stored_liability_for_solve ?? null,
    acres: block.acres, percentValue: block.solve_percent ?? null,
    coverageLevel: cov, productivityFactor: pf, share: sh,
  });
  if (recovered == null) {
    return { ...out, ok: false, status: "PRF_USE_UNRESOLVED",
             detail: "no M13 record and not enough stored detail to solve the actuarial value" };
  }
  const hits = matchRecovered(refs.cbvIndex, { cropYear, grid, recovered }, refs.tolerance);
  out.recovered_cbv = recovered;
  if (!hits.length) {
    // Nothing published lands on the value the stored productivity and share
    // imply. Before the dollar-rounded fallback, ask whether ONE published
    // value for this grid and use, at ONE share (or productivity factor),
    // reproduces every stored line. More than one candidate fitting is a
    // refusal, never a pick.
    const typeWanted = m13?.type_code || (refs.blockUse ? USE_TO_TYPE[refs.blockUse] : null) || null;
    const cands = new Map();
    const gridStr = String(Number(grid));
    for (const key of Object.keys(refs.cbvIndex || {})) {
      const [cy, st, co, gd, ty] = key.split("|");
      if (Number(cy) !== Number(cropYear) || gd !== gridStr) continue;
      if (typeWanted && ty !== typeWanted) continue;
      for (const [, v] of refs.cbvIndex[key]) {
        const c = cands.get(v) || { cbv: v, cells: [] };
        c.cells.push({ state: st, county: co, type_code: ty });
        cands.set(v, c);
      }
    }
    const fits = [];
    for (const c of cands.values()) {
      const rec = reconcileLineFactors({ lines: block.lines, cbv: c.cbv, coverageLevel: cov,
        productivityFactor: pf, share: block.share_stated ? sh : null });
      if (rec.status === "DERIVED_SHARE" || rec.status === "DERIVED_PF") fits.push({ ...c, rec });
    }
    if (fits.length === 1) {
      const f = fits[0];
      const types = [...new Set(f.cells.map((x) => x.type_code))];
      const counties = [...new Set(f.cells.map((x) => x.state + x.county))];
      if (types.length === 1) {
        return { ...out, ok: true, source: SOURCE.M13_IDENTITY_LINE_FACTOR, cbv: f.cbv,
                 protection_per_acre: round2(f.cbv * cov * f.rec.productivity_factor),
                 use: INTENDED_USE[types[0]] || types[0], type_code: types[0],
                 county: counties.length === 1 ? counties[0].slice(-3) : null,
                 county_is_established: counties.length === 1, county_candidates: counties.length,
                 line_factor: f.rec };
      }
    }
    if (fits.length > 1) out.line_factor_ambiguous = fits.map((f) => ({ cbv: f.cbv, status: f.rec.status, share: f.rec.share, productivity_factor: f.rec.productivity_factor }));
    // Nothing published matches the solved value. M13's dollar-rounded figure
    // is the last thing left, and it is LABELLED as approximate rather than
    // presented as the AIP's number.
    if (m13?.protection_per_acre != null) {
      return { ...out, ok: true, source: SOURCE.M13_PROTECTION,
               protection_per_acre: m13.protection_per_acre,
               protection_is_dollar_rounded: true,
               fallback_reason: out.lookup_failed || "PRF_CBV_NOT_PUBLISHED",
               use: m13.use, type_code: m13.type_code, county: m13.county_fips || null,
               cbv: (cov > 0 && pf > 0) ? m13.protection_per_acre / (cov * pf) : null };
    }
    return { ...out, ok: false, status: "PRF_CBV_NOT_PUBLISHED",
             detail: `solved $${recovered.toFixed(2)}/ac of base value, which RMA does not publish for grid ${grid} in ${cropYear}` };
  }
  const uses = [...new Set(hits.map((h) => h.type_code))];
  if (uses.length > 1) {
    return { ...out, ok: false, status: "PRF_USE_UNRESOLVED",
             detail: `solved value matches both ${uses.map((t) => INTENDED_USE[t] || t).join(" and ")}` };
  }
  const counties = [...new Set(hits.map((h) => h.state + h.county))];
  const vals = [...new Set(hits.map((h) => h.cbv))];
  // ⚠ A RECOVERED VALUE MATCHING ONE COUNTY DOES NOT ESTABLISH THE COUNTY.
  // Neighbouring counties in a grid frequently publish the identical value, so
  // a single match is a coincidence of the price, not evidence of location.
  // The USE is resolved (all hits agree on it) and the county is not claimed.
  if (vals.length > 1) {
    return { ...out, ok: false, status: "PRF_PRACTICE_UNRESOLVED",
             detail: `solved value is ambiguous across ${vals.length} published values` };
  }
  return { ...out, ok: true, source: out.m13_inconsistent ? SOURCE.DERIVED_OVER_M13 : SOURCE.DERIVED_IDENTITY,
           cbv: vals[0],
           protection_per_acre: round2(vals[0] * cov * pf),
           use: INTENDED_USE[uses[0]] || uses[0], type_code: uses[0],
           county: counties.length === 1 ? counties[0].slice(-3) : null,
           county_is_established: counties.length === 1,
           county_candidates: counties.length };
}

module.exports = {
  LAB, INTENDED_USE, USE_TO_TYPE, SOURCE,
  STATE_FIPS, stateFips,
  indexFieldPrf, m13AnswerFor, indexCbv, cbvFromTable,
  recoverCbv, matchRecovered, resolveCbv, reconcileLineFactors, lineLiability,
};
